// src/components/support/forms/ExtendPause.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import SpinnerLoading from "../../common/SpinnerLoading";
import FootprintDatePicker from "../../common/FootprintDatePicker";
import moment from "moment";

import { extendPause } from "../../../queryfunctions/queryfunctions";
import { useRequestProcessor } from "../../../requestProcessor";
import { SERVER_DATE_FORMAT } from "../../home/Home";
import { appConstants, selectedSupportItem } from "../../../atomstore/store";
import { useAtom } from "jotai";
import { useNavigate } from "react-router-dom";


const DEFAULT_FORMAT = "MMM DD, YYYY";
const DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS = 30;

const getMaxAllowedDateFromPauseStart = (startDate, offsetDays = DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS) => {
    const start = moment(startDate, SERVER_DATE_FORMAT);
    if (!start.isValid()) return null;
    if (start.date() === 1) return start.clone().endOf("month");
    return start.clone().add(Math.max(offsetDays - 1, 0), "days");
};

const ExtendPause = () => {
    const navigate = useNavigate();
    const [item] = useAtom(selectedSupportItem);
    const [constants] = useAtom(appConstants);
    const takeBreakMaxOffsetDaysRaw = Number(constants?.takeBreakMaxOffsetDays ?? DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS);
    const takeBreakMaxOffsetDays = Number.isFinite(takeBreakMaxOffsetDaysRaw)
        ? takeBreakMaxOffsetDaysRaw
        : DEFAULT_TAKE_BREAK_MAX_OFFSET_DAYS;

    const [endDate, setEndDate] = useState("");
    const [error, setError] = useState("");

    const { useMutationWrapper } = useRequestProcessor();

    const extendPauseMutation = useMutationWrapper(
        ["extendPause"],
        () => extendPause(item.request_id, endDate, item?.end_date)
    );

    const validate = () => {
        if (!endDate) {
            setError("Please select the new end date");
            return false;
        }

        const pauseStart = moment(item?.date, SERVER_DATE_FORMAT);
        const currentEnd = moment(item?.end_date, SERVER_DATE_FORMAT);
        const selected = moment(endDate);
        const maxAllowed = getMaxAllowedDateFromPauseStart(item?.date, takeBreakMaxOffsetDays);

        if (pauseStart.isValid() && selected.isValid() && selected.isBefore(pauseStart, "day")) {
            setError(`New end date cannot be before ${pauseStart.format(DEFAULT_FORMAT)}`);
            return false;
        }
        if (currentEnd.isValid() && selected.isValid() && !selected.isAfter(currentEnd, "day")) {
            setError(`New end date must be after ${currentEnd.format(DEFAULT_FORMAT)}`);
            return false;
        }
        if (maxAllowed && selected.isValid() && selected.isAfter(maxAllowed, "day")) {
            setError(`New end date cannot be after ${maxAllowed.format(DEFAULT_FORMAT)}`);
            return false;
        }
        return true;
    };

    const submitRequest = () => {
        if (validate()) {
            extendPauseMutation.mutate();
        }
    };

    if (!item) {
        return (
            <div className="p-4">
                <p className="text-center text-gray-600">Unable to load data</p>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {extendPauseMutation.isLoading && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Extend Pause</h2>
            </div>

            <div className="p-4 space-y-4">

                {/* Info Box */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                        {constants?.extendPauseInfoBanner ||
                            "You are extending the pause for the selected service."}
                    </p>
                </div>

                {/* Current End Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current End Date
                    </label>
                    <FootprintDatePicker
                        defaultDate={moment(item?.end_date).format(DEFAULT_FORMAT)}
                        inputProps={{ disabled: true }}
                        disabled={true}
                    />
                </div>

                {/* New End Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        New End Date
                    </label>
                    <FootprintDatePicker
                        onChange={(date) => {
                            setEndDate(date);
                            setError("");
                        }}
                        minimumDate={moment(item?.end_date, SERVER_DATE_FORMAT).add(1, "days")}
                        maximumDate={getMaxAllowedDateFromPauseStart(item?.date, takeBreakMaxOffsetDays) || moment(item?.end_date, SERVER_DATE_FORMAT).add(Math.max(takeBreakMaxOffsetDays - 1, 0), "days")}
                        inputProps={{ placeholder: "Select date" }}
                    />
                </div>

                {/* Error Message */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit */}
                <button
                    onClick={submitRequest}
                    disabled={extendPauseMutation.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>
            </div>
        </div >
    );
};

export default ExtendPause;
